import { cssVar } from '@going-once/tokens';

/**
 * Line icons for the web client's console chrome, vendored rather than installed.
 *
 * The glyphs are Lucide's — the family the design draws in and the phone already ships
 * (`lucide-react-native`). Taking the paths for the glyphs the admin and seller chrome
 * use keeps a new npm dependency, which is an Office Hours conversation, out of a PR
 * that draws blank pages. Add a glyph by copying its `iconNode` from Lucide's source —
 * `apps/mobile/node_modules/lucide-react-native/dist/esm/icons/{name}.mjs` is that source,
 * already on disk and already at the version below.
 *
 * Lucide 1.47.0 (the version `apps/mobile` resolves). Its license, verbatim:
 *
 * ISC License
 *
 * Copyright (c) 2026 Lucide Icons and Contributors
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * `chevron-down`, `chevron-up`, `chevron-right` and `radio` are derived from Feather:
 *
 * The MIT License (MIT). Copyright (c) 2013-present Cole Bemis
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify,
 * merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following
 * conditions: The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software. THE SOFTWARE IS PROVIDED "AS IS",
 * WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
 * NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR
 * OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT
 * OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

type Shape =
  | { kind: 'path'; d: string }
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'rect'; x: number; y: number; width: number; height: number; rx: number }
  /** `package`'s open box flap — the one glyph here Lucide does not draw with a closed path. */
  | { kind: 'polyline'; points: string };

const path = (d: string): Shape => ({ kind: 'path', d });

const GLYPHS = {
  store: [
    path('M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5'),
    path(
      'M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244',
    ),
    path('M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05'),
  ],
  'folder-tree': [
    path(
      'M20 10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2.5a1 1 0 0 1-.8-.4l-.9-1.2A1 1 0 0 0 15 3h-2a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z',
    ),
    path(
      'M20 21a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1h-2.9a1 1 0 0 1-.88-.55l-.42-.85a1 1 0 0 0-.92-.6H13a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z',
    ),
    path('M3 5a2 2 0 0 0 2 2h3'),
    path('M3 3v13a2 2 0 0 0 2 2h3'),
  ],
  users: [
    path('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'),
    path('M16 3.128a4 4 0 0 1 0 7.744'),
    path('M22 21v-2a4 4 0 0 0-3-3.87'),
    { kind: 'circle', cx: 9, cy: 7, r: 4 },
  ],
  radio: [
    path('M16.247 7.761a6 6 0 0 1 0 8.478'),
    path('M19.075 4.933a10 10 0 0 1 0 14.134'),
    path('M4.925 19.067a10 10 0 0 1 0-14.134'),
    path('M7.753 16.239a6 6 0 0 1 0-8.478'),
    { kind: 'circle', cx: 12, cy: 12, r: 2 },
  ],
  'chart-column': [path('M3 3v16a2 2 0 0 0 2 2h16'), path('M18 17V9'), path('M13 17V5'), path('M8 17v-3')],
  'bell-ring': [
    path('M10.268 21a2 2 0 0 0 3.464 0'),
    path('M22 8c0-2.3-.8-4.3-2-6'),
    path(
      'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
    ),
    path('M4 2C2.8 3.7 2 5.7 2 8'),
  ],
  'panel-top': [{ kind: 'rect', x: 3, y: 3, width: 18, height: 18, rx: 2 }, path('M3 9h18')],
  gift: [
    path('M12 7v14'),
    path('M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8'),
    path('M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5'),
    { kind: 'rect', x: 3, y: 7, width: 18, height: 4, rx: 1 },
  ],
  flag: [
    path(
      'M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528',
    ),
  ],
  settings: [
    path(
      'M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915',
    ),
    { kind: 'circle', cx: 12, cy: 12, r: 3 },
  ],
  'scroll-text': [
    path('M15 12h-5'),
    path('M15 8h-5'),
    path('M19 17V5a2 2 0 0 0-2-2H4'),
    path('M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3'),
  ],
  'chevron-down': [path('m6 9 6 6 6-6')],
  'chevron-up': [path('m18 15-6-6-6 6')],
  'chevron-right': [path('m9 18 6-6-6-6')],
  'sliders-horizontal': [
    path('M10 5H3'),
    path('M12 19H3'),
    path('M14 3v4'),
    path('M16 17v4'),
    path('M21 12h-9'),
    path('M21 19h-5'),
    path('M21 5h-7'),
    path('M8 10v4'),
    path('M8 12H3'),
  ],
  x: [path('M18 6 6 18'), path('m6 6 12 12')],
  // From the design's own Lucide (v0.544), for the app-wide composer (GO-643): the assistant, attaching and
  // uploading a photo, a problem, a scheduled time, and a canceled thing.
  sparkles: [
    path(
      'M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z',
    ),
    path('M20 2v4'),
    path('M22 4h-4'),
    { kind: 'circle', cx: 4, cy: 20, r: 2 },
  ],
  paperclip: [
    path(
      'm16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551',
    ),
  ],
  upload: [path('M12 3v12'), path('m17 8-5-5-5 5'), path('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4')],
  'circle-alert': [{ kind: 'circle', cx: 12, cy: 12, r: 10 }, path('M12 8v4'), path('M12 16h.01')],
  clock: [path('M12 6v6l4 2'), { kind: 'circle', cx: 12, cy: 12, r: 10 }],
  'circle-x': [{ kind: 'circle', cx: 12, cy: 12, r: 10 }, path('m15 9-6 6'), path('m9 9 6 6')],
  'message-circle-off': [
    path('m2 2 20 20'),
    path(
      'M4.93 4.929a10 10 0 0 0-1.938 11.412 2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 0 0 11.302-1.989',
    ),
    path('M8.35 2.69A10 10 0 0 1 21.3 15.65'),
  ],
  'arrow-down': [path('M12 5v14'), path('m19 12-7 7-7-7')],
  'layout-template': [
    { kind: 'rect', x: 3, y: 3, width: 18, height: 7, rx: 1 },
    { kind: 'rect', x: 3, y: 14, width: 9, height: 7, rx: 1 },
    { kind: 'rect', x: 16, y: 14, width: 5, height: 7, rx: 1 },
  ],

  // ── The seller dashboard's nav (GO-606) ────────────────────────────────────
  // The eleven of the design's fifteen seller icons the admin console never drew. They
  // are here rather than in the lazy chunk because `IconName` is this file's keys: a nav
  // naming a glyph that is not here does not typecheck, which is the point.
  'message-circle': [
    path(
      'M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719',
    ),
  ],
  layers: [
    path(
      'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
    ),
    path('M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12'),
    path('M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17'),
  ],
  video: [
    path('m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5'),
    { kind: 'rect', x: 2, y: 6, width: 14, height: 12, rx: 2 },
  ],
  film: [
    { kind: 'rect', x: 3, y: 3, width: 18, height: 18, rx: 2 },
    path('M7 3v18'),
    path('M3 7.5h4'),
    path('M3 12h18'),
    path('M3 16.5h4'),
    path('M17 3v18'),
    path('M17 7.5h4'),
    path('M17 16.5h4'),
  ],
  package: [
    path(
      'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z',
    ),
    path('M12 22V12'),
    { kind: 'polyline', points: '3.29 7 12 12 20.71 7' },
    path('m7.5 4.27 9 5.15'),
  ],
  truck: [
    path('M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2'),
    path('M15 18H9'),
    path('M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14'),
    { kind: 'circle', cx: 17, cy: 18, r: 2 },
    { kind: 'circle', cx: 7, cy: 18, r: 2 },
  ],
  megaphone: [
    path(
      'M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
    ),
    path('M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14'),
    path('M8 6v8'),
  ],
  'shield-check': [
    path(
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    ),
    path('m9 12 2 2 4-4'),
  ],
  wallet: [
    path('M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1'),
    path('M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4'),
  ],
  'chart-line': [path('M3 3v16a2 2 0 0 0 2 2h16'), path('m19 9-5 5-4-4-3 3')],
  plug: [
    path('M12 22v-5'),
    path('M15 8V2'),
    path('M17 8a1 1 0 0 1 1 1v4a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1z'),
    path('M9 8V2'),
  ],

  // ── The admin Sellers page's primitives (GO-613) ──────────────────────────
  // The ••• trigger, the toolbar's search and export, a toggle chip's check, the sort
  // arrow's other direction, the two empty states, the toast's mark, and the row and
  // header menu items the design draws on these pages.
  ellipsis: [
    { kind: 'circle', cx: 12, cy: 12, r: 1 },
    { kind: 'circle', cx: 19, cy: 12, r: 1 },
    { kind: 'circle', cx: 5, cy: 12, r: 1 },
  ],
  search: [path('m21 21-4.34-4.34'), { kind: 'circle', cx: 11, cy: 11, r: 8 }],
  download: [path('M12 15V3'), path('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'), path('m7 10 5 5 5-5')],
  check: [path('M20 6 9 17l-5-5')],
  'arrow-up': [path('m5 12 7-7 7 7'), path('M12 19V5')],
  inbox: [
    { kind: 'polyline', points: '22 12 16 12 14 15 10 15 8 12 2 12' },
    path('M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z'),
  ],
  'search-x': [
    path('m13.5 8.5-5 5'),
    path('m8.5 8.5 5 5'),
    { kind: 'circle', cx: 11, cy: 11, r: 8 },
    path('m21 21-4.3-4.3'),
  ],
  'circle-check': [{ kind: 'circle', cx: 12, cy: 12, r: 10 }, path('m16 9-5.5 5.5L8 12')],
  ban: [{ kind: 'circle', cx: 12, cy: 12, r: 10 }, path('M4.929 4.929 19.07 19.071')],
  'rotate-ccw': [path('M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8'), path('M3 3v5h5')],
  'log-out': [path('m16 17 5-5-5-5'), path('M21 12H9'), path('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4')],
  lock: [{ kind: 'rect', x: 3, y: 11, width: 18, height: 11, rx: 2 }, path('M7 11V7a5 5 0 0 1 10 0v4')],
  eye: [
    path('M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0'),
    { kind: 'circle', cx: 12, cy: 12, r: 3 },
  ],
  'eye-off': [
    path('M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49'),
    path('M14.084 14.158a3 3 0 0 1-4.242-4.242'),
    path('M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143'),
    path('m2 2 20 20'),
  ],
  pencil: [
    path(
      'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    ),
    path('m15 5 4 4'),
  ],
} satisfies Record<string, Shape[]>;

export type IconName = keyof typeof GLYPHS;

/**
 * One glyph, in the current text color. Decorative by default (`aria-hidden`): every
 * place this is drawn also carries the words, and an icon-only control names itself.
 */
export function Icon({ name }: { name: IconName }) {
  const size = cssVar('size.icon');
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      {GLYPHS[name].map((shape, index) =>
        shape.kind === 'path' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a glyph's shapes are a fixed list.
          <path key={index} d={shape.d} />
        ) : shape.kind === 'circle' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a glyph's shapes are a fixed list.
          <circle key={index} cx={shape.cx} cy={shape.cy} r={shape.r} />
        ) : shape.kind === 'polyline' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a glyph's shapes are a fixed list.
          <polyline key={index} points={shape.points} />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: a glyph's shapes are a fixed list.
          <rect key={index} x={shape.x} y={shape.y} width={shape.width} height={shape.height} rx={shape.rx} />
        ),
      )}
    </svg>
  );
}
